import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
import * as constants from '../utils/constants.js';
import { AuthContext } from './AuthContext';
import {
    DEFAULT_PIN_COLORS,
    readPinColorsFromConfiguration,
} from '../utils/configuration';
import {
    FEATURE_FLAG_KEYS,
    isFeatureFlagEnabled,
    loadFeatureFlags,
} from '../utils/featureFlags';
import { getClientTimezone } from '../utils/utcDate';
import { mergeStatusListRecords } from '../utils/driverActivityStatus';
import { normalizeList } from '../utils/apiUtils';
import { ACTIVE_DRIVE_STATUSES } from '../utils/driverLookup';
import { useLiveMapDemo } from '../demo/useLiveMapDemo';
import { isLiveMapDemoCompany } from '../demo/liveMapDemoSimulator';

const driveRecordIds = (drive) =>
    [drive?.drive_id, drive?.fk_drive_id]
        .filter((value) => value != null)
        .map(String);

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
    const { canLoadDispatcherData, activeCompanyId, isAuthReady } = useContext(AuthContext);

    const [drivers, setDrivers] = useState([]);
    const [drives, setDrives] = useState([]);
    const [refusedDrives, setRefusedDrives] = useState([]);
    const [carAssignments, setCarAssignments] = useState([]);
    const [driverStatusList, setDriverStatusList] = useState([]);
    const [driverStatusListExtended, setDriverStatusListExtended] = useState([]);
    const [configuration, setConfiguration] = useState(null);
    const [featureFlags, setFeatureFlags] = useState([]);
    const [stands, setStands] = useState([]);
    // True after the first drivers and stands responses for this board have both landed.
    const [initialBoardLoaded, setInitialBoardLoaded] = useState(false);
    // The company this board belongs to: undefined while loading, null if unknown.
    const [currentCompany, setCurrentCompany] = useState(undefined);
    const boardLoadGenerationRef = useRef(0);
    const boardLoadRef = useRef({ stands: false, drivers: false });
    // Cancelled rides stay out of the board until a refresh no longer lists them as active,
    // so an in-flight poll cannot put a just-cancelled ride back.
    const dismissedDriveIdsRef = useRef(new Set());

    const isBusyStatusEnabled = useMemo(
        () =>
            isFeatureFlagEnabled(
                featureFlags,
                FEATURE_FLAG_KEYS.IS_ENABLED_BUSY_STATUS
            ),
        [featureFlags]
    );

    // The demo company's board always shows the demo and never loads live drivers or drives.
    const isCompanyResolved = currentCompany !== undefined;
    const isDemoCompany = canLoadDispatcherData && isLiveMapDemoCompany(currentCompany);
    const liveMapDemo = useLiveMapDemo({ withBusy: isBusyStatusEnabled });
    const { start: startLiveMapDemo, stop: stopLiveMapDemo } = liveMapDemo;
    const demoFleet = liveMapDemo.fleet;

    const fetchCurrentCompany = useCallback(() => {
        return axiosInstance
            .get('/companies/current')
            .then((response) => response.data ?? null)
            .catch(() => null);
    }, []);

    const fetchConfiguration = useCallback(() => {
        return axiosInstance
            .get('/configuration')
            .then((response) => {
                setConfiguration(response.data ?? null);
                return response.data;
            })
            .catch(() => {
                setConfiguration(null);
                return null;
            });
    }, []);

    const fetchFeatureFlags = useCallback(() => {
        return loadFeatureFlags()
            .then((data) => {
                setFeatureFlags(data);
                return data;
            })
            .catch(() => {
                setFeatureFlags([]);
                return [];
            });
    }, []);

    const fetchStands = useCallback(() => {
        return axiosInstance
            .get('/stands')
            .then((response) => {
                const list = normalizeList(response.data);
                setStands(list);
                return list;
            })
            .catch(() => {
                setStands([]);
                return [];
            });
    }, []);

    const resetDispatcherData = useCallback(() => {
        setDrivers([]);
        setDrives([]);
        setRefusedDrives([]);
        setCarAssignments([]);
        setDriverStatusList([]);
        setDriverStatusListExtended([]);
        setConfiguration(null);
        setFeatureFlags([]);
        setStands([]);
        setCurrentCompany(undefined);
        dismissedDriveIdsRef.current = new Set();
        boardLoadGenerationRef.current += 1;
        boardLoadRef.current = { stands: false, drivers: false };
        setInitialBoardLoaded(false);
    }, []);

    const beginBoardLoad = () => {
        boardLoadGenerationRef.current += 1;
        boardLoadRef.current = { stands: false, drivers: false };
        setInitialBoardLoaded(false);
        return boardLoadGenerationRef.current;
    };

    const markBoardSourceLoaded = (generation, source) => {
        if (generation !== boardLoadGenerationRef.current) return;
        boardLoadRef.current[source] = true;
        if (boardLoadRef.current.stands && boardLoadRef.current.drivers) {
            setInitialBoardLoaded(true);
        }
    };

    useEffect(() => {
        if (!canLoadDispatcherData) {
            resetDispatcherData();
            return undefined;
        }

        // On a board reload the access token is restored asynchronously. Fetching
        // before that finishes can store an empty board until the next 10s poll.
        if (!isAuthReady) {
            return undefined;
        }

        fetchConfiguration();
        const generation = beginBoardLoad();
        fetchStands().finally(() => markBoardSourceLoaded(generation, 'stands'));

        // Feature flags come first so a demo starts with the right busy setting.
        let cancelled = false;
        setCurrentCompany(undefined);
        Promise.all([fetchCurrentCompany(), fetchFeatureFlags()]).then(([company]) => {
            if (!cancelled) setCurrentCompany(company);
        });
        return () => {
            cancelled = true;
        };
    }, [
        activeCompanyId,
        canLoadDispatcherData,
        isAuthReady,
        fetchConfiguration,
        fetchCurrentCompany,
        fetchFeatureFlags,
        fetchStands,
        resetDispatcherData,
    ]);

    useEffect(() => {
        if (isDemoCompany) startLiveMapDemo();
        else stopLiveMapDemo();
    }, [isDemoCompany, startLiveMapDemo, stopLiveMapDemo]);

    const withoutDismissedActiveDrives = useCallback((list) => {
        const dismissed = dismissedDriveIdsRef.current;
        if (dismissed.size === 0 || !Array.isArray(list)) return list;

        const stillActive = new Set();
        const next = [];
        list.forEach((drive) => {
            const ids = driveRecordIds(drive);
            const dismissedId = ids.find((id) => dismissed.has(id));
            if (dismissedId && ACTIVE_DRIVE_STATUSES.includes(drive?.status)) {
                stillActive.add(dismissedId);
                return;
            }
            next.push(drive);
        });
        [...dismissed].forEach((id) => {
            if (!stillActive.has(id)) dismissed.delete(id);
        });
        return next;
    }, []);

    const fetchDrives = useCallback(() => {
        const timezone = getClientTimezone();
        axiosInstance
            .get('/drives/details', { params: { days_ago: 0, timezone } })
            .then(response => setDrives(withoutDismissedActiveDrives(response.data)))
            .catch(() => {});
    }, [withoutDismissedActiveDrives]);

    useEffect(() => {
        if (!isAuthReady || !canLoadDispatcherData || !isCompanyResolved || isDemoCompany) {
            return;
        }

        const generation = boardLoadGenerationRef.current;
        let driversMarked = false;
        const noteDriversLoaded = (loaded) => {
            if (!loaded || driversMarked) return;
            driversMarked = true;
            markBoardSourceLoaded(generation, 'drivers');
        };

        fetchDrivers().then(noteDriversLoaded);
        fetchDrives();
        fetchRefusedDrives();
        fetchCarAssignments();
        fetchDriverStatusList();

        const intervalId = setInterval(() => {
            fetchDrivers().then(noteDriversLoaded);
            fetchDrives();
            fetchRefusedDrives();
            fetchCarAssignments();
            fetchDriverStatusList();
        }, constants.REACT_APP_REFRESH_INTERVAL);

        return () => clearInterval(intervalId);
    }, [
        isAuthReady,
        activeCompanyId,
        canLoadDispatcherData,
        isCompanyResolved,
        isDemoCompany,
        fetchDrives,
    ]);

    const fetchDrivers = () => {
        return axiosInstance
            .get('/drivers')
            .then(response => {
                const list = Array.isArray(response.data) ? response.data : [];
                setDrivers(list);
                return true;
            })
            .catch(() => false);
    };

    const applyDriverActivityStatus = useCallback((driverId, activityStatus) => {
        if (driverId == null) return;
        const key = String(driverId);
        setDrivers((current) =>
            Array.isArray(current)
                ? current.map((driver) =>
                    String(driver?.driver_id) === key
                        ? { ...driver, activity_status: activityStatus }
                        : driver
                )
                : current
        );
    }, []);

    const removeDrive = useCallback((driveId) => {
        if (driveId == null) return;
        const id = String(driveId);
        dismissedDriveIdsRef.current.add(id);
        setDrives((current) =>
            Array.isArray(current)
                ? current.filter((drive) => !driveRecordIds(drive).includes(id))
                : current
        );
    }, []);

    const fetchRefusedDrives = () => {
        const timezone = getClientTimezone();
        axiosInstance
            .get('/drives/refused', { params: { days_ago: 0, timezone } })
            .then(response => setRefusedDrives(response.data))
            .catch(() => {});
    };

    const fetchCarAssignments = () => {
        axiosInstance
            .get('/drivers/car_assigned')
            .then((response) => {
                const data = response.data;
                setCarAssignments(Array.isArray(data) ? data : []);
            })
            .catch(() => {});
    };

    const fetchDriverStatusList = () => {
        const timezone = getClientTimezone();
        const statusListParams = { timezone, clientTimezone: timezone };
        Promise.allSettled([
            axiosInstance.get('/drivers/status_list', {
                params: { days_ago: 0, ...statusListParams },
            }),
            // Unfiltered: round start can be before today (overnight / previous days).
            axiosInstance.get('/drivers/status_list'),
        ]).then(([todayResult, historyResult]) => {
            const today =
                todayResult.status === 'fulfilled' &&
                Array.isArray(todayResult.value.data)
                    ? todayResult.value.data
                    : [];
            const history =
                historyResult.status === 'fulfilled' &&
                Array.isArray(historyResult.value.data)
                    ? historyResult.value.data
                    : [];
            setDriverStatusList(today);
            setDriverStatusListExtended(
                mergeStatusListRecords(history, today)
            );
        });
    };

    const pinColors = useMemo(
        () =>
            configuration
                ? readPinColorsFromConfiguration(configuration)
                : { ...DEFAULT_PIN_COLORS },
        [configuration]
    );

    return (
        <DataContext.Provider
            value={{
                drivers: demoFleet ? liveMapDemo.drivers : drivers,
                drives: demoFleet ? liveMapDemo.drives : drives,
                refusedDrives: demoFleet ? demoFleet.refusedDrives : refusedDrives,
                carAssignments: demoFleet ? demoFleet.carAssignments : carAssignments,
                driverStatusList: demoFleet ? demoFleet.statusList : driverStatusList,
                driverStatusListExtended: demoFleet
                    ? demoFleet.statusList
                    : driverStatusListExtended,
                configuration,
                featureFlags,
                stands: demoFleet ? demoFleet.stands : stands,
                initialBoardLoaded: demoFleet != null || initialBoardLoaded,
                pinColors,
                isBusyStatusEnabled,
                refreshConfiguration: fetchConfiguration,
                refreshStands: fetchStands,
                applyDriverActivityStatus,
                refreshLiveBoard: () => {
                    fetchDrivers();
                    fetchDrives();
                    fetchCarAssignments();
                    fetchDriverStatusList();
                },
                isLiveMapDemoActive: demoFleet != null,
                cancelLiveMapDemoDrive: liveMapDemo.cancelDrive,
                removeDrive,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};
